import assertNever from 'assert-never';

import {
	ExternalDisplays,
	IpMode,
	MissingFeatureType,
	RfChannel,
	DeviceConferencingPlatform
} from '@shure/cloud/shared/models/devices';
import { mapBatteryStateFromSysApi, mapDurationFromSysApi } from '@shure/shared/angular/data-access/system-api/mappers';
import {
	DeviceCompatibilityLevel,
	DeviceMicState,
	DeviceModel
} from '@shure/shared/angular/data-access/system-api/models';
import { PLACEHOLDER_VALUE } from '@shure/shared/models';

import {
	CompatibilityLevel,
	ExternalDisplayPropertiesFeatureFragment,
	InventoryDeviceFragment,
	PropertyPanelDeviceFragment,
	PropertyPanelRfChannelFragment,
	PropertyPanelRfChannelLinkedDeviceFragment,
	IpMode as SysApiMode,
	MicStatus,
	MicStatusV2,
	ConferencingPlatform
} from '../graphql/generated/cloud-sys-api';

import { mapMicStatusFromSysApi } from './map-mic-status';

export function mapFeatureValueFromSysApi(value?: string | null, defaultValue = PLACEHOLDER_VALUE): string {
	return value ?? defaultValue;
}

export function mapHostedFirmwareVersionFromSysApi(value?: string | null): string {
	// hosted devices return '0.0.0.0' when they don't have a hosted firmware version
	if (!value || value === '0.0.0.0') {
		return '';
	}
	return value;
}

export function mapIpModeFromSysApi(value: SysApiMode | null | undefined): IpMode | undefined {
	if (!value) {
		return undefined;
	}

	switch (value) {
		case SysApiMode.Dhcp:
			return IpMode.DHCP;
		case SysApiMode.Static:
			return IpMode.STATIC;
		default:
			assertNever(value);
	}
}

/**
 * TODO: Remove this mapping and use the value directly from GraphQL. We want to make this change
 * as part of a larger refactoring to create a default DeviceImageProvider, which will allow us
 * to remove all of the individual device behavior projects:
 * https://bitbucket.shure.com/projects/WPLT/repos/shure.web.apps/pull-requests/3859/overview?commentId=176994
 */
export function mapDeviceModelFromSysApi(model: string): DeviceModel {
	switch (model) {
		case 'MXA920-S':
			return DeviceModel.MXA920S;
		case 'MXA920-R':
			return DeviceModel.MXA920R;
		case 'P300':
			return DeviceModel.P300;
		case 'MXWAPXD2':
			return DeviceModel.MXWAPXD2;
		case 'MXA710-4FT':
			return DeviceModel.MXA7104FT;
		case 'MXA710-2FT':
			return DeviceModel.MXA7102FT;
		case 'ANIUSB-MATRIX':
			return DeviceModel.ANIUSB;
		case 'MXA901-R':
			return DeviceModel.MXA901R;
		case 'MXA901-S':
			return DeviceModel.MXA901S;
		case 'MXA902-S':
			return DeviceModel.MXA902S;
		case 'MXW1X':
			return DeviceModel.MXW1X;
		case 'MXW2X':
			return DeviceModel.MXW2X;
		case 'MXW6X':
			return DeviceModel.MXW6X;
		case 'MXW8X':
			return DeviceModel.MXW8X;
		case 'IMX-Room':
			return DeviceModel.IMXROOM;
		case 'MXN5-C':
			return DeviceModel.MXN5C;
		case 'ANX4':
			return DeviceModel.MCR;
		case 'AD600':
			return DeviceModel.AD600;
		case 'ADTD':
			return DeviceModel.ADTD;
		case 'ADTQ':
			return DeviceModel.ADTQ;
		default:
			return <DeviceModel>model;
	}
}

export function mapLastBootTimeFromSysApi(lastBootTime: string | undefined): string {
	if (!lastBootTime || lastBootTime === PLACEHOLDER_VALUE) {
		return '';
	}

	return lastBootTime;
}

export function mapRfChannelsFromSysApi(rfChannels: PropertyPanelRfChannelFragment[]): RfChannel[] {
	const channels: RfChannel[] = [];

	for (const rfChannel of rfChannels) {
		const linkedDevice = rfChannel.features.linkedTransmitter?.linkage?.device;
		if (!linkedDevice) {
			channels.push({});
			continue;
		}

		const descriptionFeatures = linkedDevice.description.features;
		const features = linkedDevice.features;
		channels.push({
			linkedDevice: {
				id: linkedDevice.id,
				name: mapFeatureValueFromSysApi(features.name?.name ?? ''),
				status: linkedDevice.status,
				model: mapDeviceModelFromSysApi(linkedDevice.interface.model ?? ''),
				features: {
					mute: descriptionFeatures.audioMute.supported
						? {
								muted: features.audioMute?.muted || false,
								isMissing: !features.audioMute
							}
						: undefined,
					identify: descriptionFeatures.identify.supported
						? {
								identifying: features.identify?.identifying || false,
								isMissing: !features.identify
							}
						: undefined,
					micStatus: deviceFragmentToMicStateFeature(linkedDevice),
					batteryLevel: descriptionFeatures.batteryLevel.supported
						? {
								percentage: features.batteryLevel?.percentage ?? Number.NaN,
								currentState: mapBatteryStateFromSysApi(features.batteryLevel?.status.currentState),
								targetState: mapBatteryStateFromSysApi(features.batteryLevel?.status.targetState),
								timeToTargetState: mapDurationFromSysApi(
									features.batteryLevel?.status.timeToTargetState
								),
								isMissing: !features.batteryLevel
							}
						: undefined
				}
			}
		});
	}

	return channels;
}

export function mapCompatibilityLevelFromSysApi(compatibility: CompatibilityLevel): DeviceCompatibilityLevel {
	switch (compatibility) {
		case CompatibilityLevel.IncompatibleTooOld:
			return DeviceCompatibilityLevel.IncompatibleTooOld;
		case CompatibilityLevel.CompatibleOld:
			return DeviceCompatibilityLevel.CompatibleOld;
		case CompatibilityLevel.CompatibleLatest:
			return DeviceCompatibilityLevel.CompatibleLatest;
		case CompatibilityLevel.CompatibleNew:
		case CompatibilityLevel.IncompatibleTooNew:
			// We don't have the concept of IncompatibleTooNew in Cloud, so we map that to CompatibleNew
			return DeviceCompatibilityLevel.CompatibleNew;
		default:
			assertNever(compatibility);
	}
}

export function mapExternalDisplaysFromSysApi(displays: ExternalDisplayPropertiesFeatureFragment[]): ExternalDisplays {
	return {
		displays: displays.map((display) => ({
			id: display.id,
			connected: display.features.connection?.connected ?? false,
			resolution: display.features.resolution?.resolution ?? ''
		}))
	};
}

export function deviceFragmentToMicStateFeature(
	device: InventoryDeviceFragment | PropertyPanelDeviceFragment | PropertyPanelRfChannelLinkedDeviceFragment
): ({ status: DeviceMicState } & MissingFeatureType) | undefined {
	// extract the micStatus V1 and V2 feature descriptions and values from the device
	const {
		description: {
			features: { micStatus: micStatusSupported, micStatusV2: micStatusV2Supported }
		},
		features: { micStatus, micStatusV2 }
	} = device;

	// If neither are supported, return undefined
	if (!micStatusSupported.supported && !micStatusV2Supported.supported) {
		return undefined;
	}

	let sysApiMicStatus: MicStatus | MicStatusV2 | undefined | null;
	if (micStatusSupported.supported && micStatus) {
		sysApiMicStatus = micStatus.status;
	} else if (micStatusV2Supported.supported && micStatusV2) {
		sysApiMicStatus = micStatusV2.status;
	} else {
		sysApiMicStatus = undefined;
	}
	return {
		status: mapMicStatusFromSysApi(sysApiMicStatus),
		isMissing: !sysApiMicStatus
	};
}

export function mapConferencingPlatformFromSysApi(
	platform?: ConferencingPlatform
): DeviceConferencingPlatform | undefined {
	switch (platform) {
		case ConferencingPlatform.Zoom:
			return DeviceConferencingPlatform.Zoom;
		case ConferencingPlatform.MicrosoftTeams:
			return DeviceConferencingPlatform.MicrosoftTeams;
		case undefined:
			return undefined;
		default:
			assertNever(platform);
	}
}
